import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import type { RxDatabase } from 'rxdb';
import { RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql';

import { apiConfig } from '../config/api.config';
import { AimsDatabaseCollections } from '../models/models';
import { assetDetailsPullQueryBuilder } from './assetDetails/queryBuilder';
import { assetsByAreaHeirachyPullQueryBuilder } from './assetsByAreaHeirachy/queryBuilder';
import { assetSectionDefinationsListPullQueryBuilder } from './assetSectionDefination/queryBuilder';
import {
  componentChecklistPullQueryBuilder,
  componentChecklistPushModifier,
  componentChecklistPushQueryBuilder,
} from './componentChecklist/queryBuilder';
//import { plannedDeficiencyFilesPullQueryBuilder } from './plannedDeficiencyFiles/queryBuilder';
import { componentRemovalCategoriesPullQueryBuilder } from './componentRemovalCategories/queryBuilder';
import { componentTypePullQueryBuilder } from './componentType/queryBuilder';
import { conditionRatingPullQueryBuilder } from './conditionRating/queryBuilder';
import { deficiencyActionedListPullQueryBuilder } from './deficiencyActionedList/queryBuilder';
import {
  deficiencyCommentsPullQueryBuilder,
  deficiencyVersionCommentsPullQueryBuilder,
} from './deficiencyComments/queryBuilder';
import {
  deficiencyComponentRemovalPullQueryBuilder,
  deficiencyComponentRemovalPushModifier,
  deficiencyComponentRemovalPushQueryBuilder,
} from './deficiencyComponentRemoval/queryBuilder';
import {
  deficiencyFilesPullQueryBuilder,
  deficiencyFilesPushModifier,
  deficiencyFilesPushQueryBuilder,
} from './deficiencyFiles/queryBuilder';
import {
  deficiencyListPullQueryBuilder,
  deficiencyListPushModifier,
  deficiencyListPushQueryBuilder,
} from './deficiencyList/queryBuilder';
import {
  deficiencyVersionFilesPullQueryBuilder,
  deficiencyVersionFilesPushModifier,
  deficiencyVersionFilesPushQueryBuilder,
} from './deficiencyVersionFiles/queryBuilder';
import { elevationRangePullQueryBuilder } from './elevationRange/queryBuilder';
import { environmentalConditionPullQueryBuilder } from './environmentalCondition/queryBuilder';
import { handleReplicationErrors } from './handleReplicationErrors';
/* 
  Planned Deficiency Related code commented in this file. It's for future use.
 */
//import { plannedDeficiencyListPullQueryBuilder } from './existingDeficiencyList/queryBuilder';
import {
  inspectionFilesPullQueryBuilder,
  inspectionFilesPushModifier,
  inspectionFilesPushQueryBuilder,
} from './inspectionFiles/queryBuilder';
import { inspectionListPullQueryBuilder } from './inspectionList/queryBuilder';
import { lineandSectionPropertiesListPullQueryBuilder } from './lineandSectionPropertiesDetails/queryBuilder';
import { LiveInterval } from './LiveInterval';
import {
  notificationPullQueryBuilder,
  notificationPushQueryBuilder,
} from './notification/queryBuilder';
import { operationalDowntimePullQueryBuilder } from './operationalDowntime/queryBuilder';
import { pmCheckListPullQueryBuilder } from './pmCheckList/queryBuilder';
import { previousReportFilesPullQueryBuilder } from './previousReports/queryBuilder';
import { previousTaskCommentsPullQueryBuilder } from './previousTaskComments/queryBuilder';
import { primaryCauseOfFailurePullQueryBuilder } from './primaryCauseOfFailure/queryBuilder';
import { remedialActionPullQueryBuilder } from './remedialAction/queryBuilder';
import { repairFactorPullQueryBuilder } from './repairFactor/queryBuilder';
import { RxdbCollectionName } from './rxdbCollectionName';
import {
  savePmChecklistPullQueryBuilder,
  savePmChecklistPushModifier,
  savePmChecklistPushQueryBuilder,
} from './savepmChecklist/queryBuilder';
import {
  signAndStampFilePullQueryBuilder,
  signAndStampFilePushModifier,
  signAndStampFilePushQueryBuilder,
} from './signAndStamp/queryBuilder';
import { signAndStampUserConsentPullQueryBuilder } from './signAndStampUserConsent/queryBuilder';
import {
  signAndStampUserLevelPullQueryBuilder,
  signAndStampUserLevelPushModifier,
  signAndStampUserLevelPushQueryBuilder,
} from './signAndStampUserLevel/queryBuilder';
import {
  taskListPullModifier,
  taskListPullQueryBuilder,
  taskListPushModifier,
  taskListPushQueryBuilder,
} from './taskList/queryBuilder';
import { trafficSafetyScorePullQueryBuilder } from './trafficSafetyScore/queryBuilder';
import { urgencyPullQueryBuilder } from './urgency/queryBuilder';
import { viewInspectorPullQueryBuilder } from './viewInspector/queryBuilder';
import {
  workPackageFilesPullModifier,
  workPackageFilesPullQueryBuilder,
} from './workPackageFiles/queryBuilder';
import { workPackagesPullQueryBuilder } from './workPackages/queryBuilder';

const defaultModifer = (doc: unknown) => {
  return doc;
};
const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';
// eslint-disable-next-line no-console
if (DEBUG_RXDB) console.log('RxDB Debugging Enabled...');

const syncGraphQLFor = (
  idToken: string,
  rxdb: RxDatabase<AimsDatabaseCollections> | undefined,
  collectionName: string,
  sync_interval: number,
  pullQueryBuilder: unknown,
  pullModifier?: unknown,
  pushQueryBuilder?: unknown,
  pushModifier?: unknown,
) => {
  // eslint-disable-next-line no-console
  console.log(`Begin RXDB Pull Replication on collection ${collectionName}`);
  const configql: unknown = {
    url: `${apiConfig.graphUrl}?collection=${collectionName}`,
    headers: { Authorization: `Bearer ${idToken}` },
    pull: {
      queryBuilder: pullQueryBuilder,
      modifier: pullModifier || defaultModifer,
    },
    deletedFlag: 'isDeleted',
    live: true,
    liveInterval: sync_interval,
    retryTime: 3000,
  };
  if (DEBUG_RXDB) console.log(`Config: ${JSON.stringify(configql)}`);

  if (pushQueryBuilder) {
    console.log(`Begin RXDB Push/Update for ${collectionName}`);
    configql.push = {
      queryBuilder: pushQueryBuilder,
      batchSize: 1,
      modifier: pushModifier || defaultModifer,
    };
  }

  const state: RxGraphQLReplicationState<unknown> =
    rxdb?.collections[collectionName]?.syncGraphQL(configql);

  return state;
};

export default async (idToken: string, db: RxDatabase<AimsDatabaseCollections> | undefined) => {
  const syncStates: unknown[] = [];

  const notificationState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.NOTIFICATION,
    LiveInterval.MINUTES_10,
    notificationPullQueryBuilder,
    undefined,
    notificationPushQueryBuilder,
  );
  if (notificationState) {
    syncStates.push(notificationState);
  }

  const viewInspectorState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.VIEW_INSPECTOR,
    LiveInterval.MINUTES_10,
    viewInspectorPullQueryBuilder,
  );
  if (viewInspectorState) {
    syncStates.push(viewInspectorState);
  }

  const workPackageState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.WORK_PACKAGES,
    LiveInterval.MINUTES_10,
    workPackagesPullQueryBuilder,
  );
  if (workPackageState) {
    syncStates.push(workPackageState);
  }

  const inspectionListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.INSPECTION_LIST,
    LiveInterval.MINUTES_10,
    inspectionListPullQueryBuilder,
  );
  if (inspectionListState) {
    syncStates.push(inspectionListState);
  }

  const taskListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.TASK_LIST,
    LiveInterval.MINUTES_10,
    taskListPullQueryBuilder,
    taskListPullModifier,
    taskListPushQueryBuilder,
    taskListPushModifier,
  );
  if (taskListState) {
    syncStates.push(taskListState);
  }

  const workPackageFilesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.WORK_PACKAGE_FILES,
    LiveInterval.MINUTES_10,
    workPackageFilesPullQueryBuilder,
    workPackageFilesPullModifier,
  );
  if (workPackageFilesState) {
    syncStates.push(workPackageFilesState);
  }

  const assetDetailsState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.ASSET_DETAILS,
    LiveInterval.MINUTES_10,
    assetDetailsPullQueryBuilder,
  );
  if (assetDetailsState) {
    syncStates.push(assetDetailsState);
  }

  const savepmChecklistState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.SAVE_PMCHECKLIST,
    LiveInterval.MINUTES_10,
    savePmChecklistPullQueryBuilder,
    undefined,
    savePmChecklistPushQueryBuilder,
    savePmChecklistPushModifier,
  );
  if (savepmChecklistState) {
    syncStates.push(savepmChecklistState);
  }

  const inspectionFilesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.INSPECTION_FILES,
    LiveInterval.MINUTES_10,
    inspectionFilesPullQueryBuilder,
    undefined,
    inspectionFilesPushQueryBuilder,
    inspectionFilesPushModifier,
  );
  if (inspectionFilesState) {
    syncStates.push(inspectionFilesState);
  }

  const signAndStampFileState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.SIGN_AND_STAMP_FILE,
    LiveInterval.MINUTES_10,
    signAndStampFilePullQueryBuilder,
    undefined,
    signAndStampFilePushQueryBuilder,
    signAndStampFilePushModifier,
  );
  if (signAndStampFileState) {
    syncStates.push(signAndStampFileState);
  }

  const previousReportFilesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.PREVIOUS_REPORTS_FILES,
    LiveInterval.MINUTES_10,
    previousReportFilesPullQueryBuilder,
  );
  if (previousReportFilesState) {
    syncStates.push(previousReportFilesState);
  }

  const deficiencyListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_LIST,
    LiveInterval.MINUTES_10,
    deficiencyListPullQueryBuilder,
    undefined,
    deficiencyListPushQueryBuilder,
    deficiencyListPushModifier,
  );
  if (deficiencyListState) {
    syncStates.push(deficiencyListState);
  }

  const deficiencyActionedListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_ACTIONED_LIST,
    LiveInterval.MINUTES_10,
    deficiencyActionedListPullQueryBuilder,
    undefined,
  );
  if (deficiencyActionedListState) {
    syncStates.push(deficiencyActionedListState);
  }

  const componentRemovalCategoriesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.COMPONENT_REMOVAL_CATEGORIES,
    LiveInterval.HOURS_1,
    componentRemovalCategoriesPullQueryBuilder,
  );
  if (componentRemovalCategoriesState) {
    syncStates.push(componentRemovalCategoriesState);
  }

  const deficiencyComponentRemovalState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_COMPONENT_REMOVAL,
    LiveInterval.MINUTES_10,
    deficiencyComponentRemovalPullQueryBuilder,
    undefined,
    deficiencyComponentRemovalPushQueryBuilder,
    deficiencyComponentRemovalPushModifier,
  );
  if (deficiencyComponentRemovalState) {
    syncStates.push(deficiencyComponentRemovalState);
  }

  const deficiencyFilesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_FILES,
    LiveInterval.MINUTES_10,
    deficiencyFilesPullQueryBuilder,
    undefined,
    deficiencyFilesPushQueryBuilder,
    deficiencyFilesPushModifier,
  );
  if (deficiencyFilesState) {
    syncStates.push(deficiencyFilesState);
  }

  const remedialActionState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.REMEDIAL_ACTION,
    LiveInterval.HOURS_1,
    remedialActionPullQueryBuilder,
  );
  if (remedialActionState) {
    syncStates.push(remedialActionState);
  }

  const primaryCauseOfFailureState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.PRIMARY_CAUSE_OF_FAILURE,
    LiveInterval.HOURS_1,
    primaryCauseOfFailurePullQueryBuilder,
  );
  if (primaryCauseOfFailureState) {
    syncStates.push(primaryCauseOfFailureState);
  }

  const conditionRatingState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.CONDITION_RATING,
    LiveInterval.HOURS_1,
    conditionRatingPullQueryBuilder,
  );
  if (conditionRatingState) {
    syncStates.push(conditionRatingState);
  }

  const operationalDowntimeState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.OPERATIONAL_DOWNTIME,
    LiveInterval.HOURS_1,
    operationalDowntimePullQueryBuilder,
  );
  if (operationalDowntimeState) {
    syncStates.push(operationalDowntimeState);
  }

  const trafficSafetyScoreState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.TRAFFIC_SAFETY_SCORE,
    LiveInterval.HOURS_1,
    trafficSafetyScorePullQueryBuilder,
  );
  if (trafficSafetyScoreState) {
    syncStates.push(trafficSafetyScoreState);
  }

  const environmentalConditionState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.ENVIRONMENTAL_CONDITION,
    LiveInterval.HOURS_1,
    environmentalConditionPullQueryBuilder,
  );
  if (environmentalConditionState) {
    syncStates.push(environmentalConditionState);
  }

  const repairFactorState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.REPAIR_FACTOR,
    LiveInterval.HOURS_1,
    repairFactorPullQueryBuilder,
  );
  if (repairFactorState) {
    syncStates.push(repairFactorState);
  }

  const urgencyState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.URGENCY,
    LiveInterval.HOURS_1,
    urgencyPullQueryBuilder,
  );
  if (urgencyState) {
    syncStates.push(urgencyState);
  }

  const componentTypeState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.COMPONENT_TYPE,
    LiveInterval.HOURS_1,
    componentTypePullQueryBuilder,
  );
  if (componentTypeState) {
    syncStates.push(componentTypeState);
  }

  const componentChecklistState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.COMPONENT_CHECKLIST,
    LiveInterval.MINUTES_10,
    componentChecklistPullQueryBuilder,
    undefined,
    componentChecklistPushQueryBuilder,
    componentChecklistPushModifier,
  );
  if (componentChecklistState) {
    syncStates.push(componentChecklistState);
  }

  const pmCheckListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.PM_CHECKLIST,
    LiveInterval.MINUTES_10,
    pmCheckListPullQueryBuilder,
  );
  if (pmCheckListState) {
    syncStates.push(pmCheckListState);
  }

  const assetsByAreaHeirachyState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.ASSETS_BY_AREA_HEIRACHY,
    LiveInterval.HOURS_1,
    assetsByAreaHeirachyPullQueryBuilder,
  );
  if (assetsByAreaHeirachyState) {
    syncStates.push(assetsByAreaHeirachyState);
  }

  const previousTaskCommentsState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.PREVIOUS_TASK_COMMENTS,
    LiveInterval.MINUTES_10,
    previousTaskCommentsPullQueryBuilder,
  );
  if (previousTaskCommentsState) {
    syncStates.push(previousTaskCommentsState);
  }

  const deficiencyCommentsState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_COMMENTS,
    LiveInterval.MINUTES_10,
    deficiencyCommentsPullQueryBuilder,
  );
  if (deficiencyCommentsState) {
    syncStates.push(deficiencyCommentsState);
  }

  const signAndStampUserConsentState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.SIGN_AND_STAMP_USER_CONSENT,
    LiveInterval.MINUTES_10,
    signAndStampUserConsentPullQueryBuilder,
  );
  if (signAndStampUserConsentState) {
    syncStates.push(signAndStampUserConsentState);
  }

  const signAndStampUserLevelState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.SIGN_AND_STAMP_USER_LEVEL,
    LiveInterval.MINUTES_10,
    signAndStampUserLevelPullQueryBuilder,
    undefined,
    signAndStampUserLevelPushQueryBuilder,
    signAndStampUserLevelPushModifier,
  );
  if (signAndStampUserLevelState) {
    syncStates.push(signAndStampUserLevelState);
  }

  const elevationRangeState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.ELEVATION_RANGE,
    LiveInterval.MINUTES_10,
    elevationRangePullQueryBuilder,
  );
  if (elevationRangeState) {
    syncStates.push(elevationRangeState);
  }

  /* 
  Planned Deficiency Related code commented below. It's for future use.
 */

  // const plannedDeficiencyListState = syncGraphQLFor(
  //   idToken,
  //   db,
  //   RxdbCollectionName.PLANNED_DEFICIENCY_LIST,
  //   LiveInterval.MINUTES_10,
  //   plannedDeficiencyListPullQueryBuilder,
  // );
  // if (plannedDeficiencyListState) {
  //   syncStates.push(plannedDeficiencyListState);
  // }

  // const plannedDeficiencyFilesState = syncGraphQLFor(
  //   idToken,
  //   db,
  //   RxdbCollectionName.PLANNED_DEFICIENCY_FILES,
  //   LiveInterval.MINUTES_10,
  //   plannedDeficiencyFilesPullQueryBuilder,
  // );
  // if (plannedDeficiencyFilesState) {
  //   syncStates.push(plannedDeficiencyFilesState);
  // }

  const deficiencyVersionFilesState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_VERSION_FILES,
    LiveInterval.MINUTES_10,
    deficiencyVersionFilesPullQueryBuilder,
    undefined,
    deficiencyVersionFilesPushQueryBuilder,
    deficiencyVersionFilesPushModifier,
  );
  if (deficiencyVersionFilesState) {
    syncStates.push(deficiencyVersionFilesState);
  }

  const deficiencyVersionCommentsState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.DEFICIENCY_VERSION_COMMENTS,
    LiveInterval.MINUTES_10,
    deficiencyVersionCommentsPullQueryBuilder,
  );
  if (deficiencyVersionCommentsState) {
    syncStates.push(deficiencyVersionCommentsState);
  }

  const lineAndSectionPropertiesListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.LINE_AND_SECTION_PROPERTIES_LIST,
    LiveInterval.MINUTES_10,
    lineandSectionPropertiesListPullQueryBuilder,
  );
  if (lineAndSectionPropertiesListState) {
    syncStates.push(lineAndSectionPropertiesListState);
  }

  const assetSectionDefinationsListState = syncGraphQLFor(
    idToken,
    db,
    RxdbCollectionName.ASSET_SECTION_DEFINATIONS_LIST,
    LiveInterval.MINUTES_10,
    assetSectionDefinationsListPullQueryBuilder,
  );
  if (assetSectionDefinationsListState) {
    syncStates.push(assetSectionDefinationsListState);
  }

  syncStates.forEach(state => handleReplicationErrors(state));

  return { syncStates };
};
